import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button, Divider } from 'components/UIElements'
import {
  QUESTION_TYPE_MAP,
  QUESTION_TYPES_WITH_CHOICES,
  QUESTION_TYPES_WITH_SCORING,
  QUESTION_TYPES_WITH_OPT_OUT,
  SELECT_QUESTION_TYPES,
  QUESTION_ATTRIBUTE_MAP,
  MAX_PHOTO_LIMIT_FIELD_DEFAULT_VALUE,
} from 'utils/constants'
import STRINGS from 'utils/strings'
import { scrollTo } from 'utils/misc'
import AttributePanel from './AttributePanel'
import ScoringPanel from '../Scoring/ScoringPanel'
import QuestionPanel from '../QuestionPanel'
import ValidationPanel from '../ValidationPanel'
import LogicPanelContainer from '../../containers/LogicPanelContainer'
import { itemEditorActions } from '../../modules/Survey'
import InReviewPanel from './InReviewPanel'
import MaxPhotoLimitPanel from './MaxPhotoLimitPanel'
import SelectionRulesPanel from './SelectionRulesPanel'
import TimedMatchingGridConfigPanel from './TimedMatchingGridConfigPanel'
import CommentsPanel from '../../../EditClinro/components/Comments/CommentsPanel'
import SiblingsPanel from './Siblings/SiblingsPanel'

const introPic = require('../../assets/intro_with_greeting.png')
const introNoGreetingPic = require('../../assets/intro_no_greeting.png')
const timerPic = require('../../assets/survey-timer.png')

class ItemEditor extends React.Component {
  componentDidUpdate(prevProps) {
    const { activeItemId, toggleItemAttribute, setItemAttribute, survey } = this.props
    const item = survey.questions[activeItemId]
    if (!item) return
    const { attributes } = item

    if (activeItemId !== prevProps.activeItemId) {
      const editorEl = document.querySelector('.right-section .item-editor')
      if (editorEl) editorEl.scrollTop = 0
      this.onScrollToItem({})

      if (
        item.type !== QUESTION_TYPE_MAP.timedMatchingGrid &&
        (attributes[QUESTION_ATTRIBUTE_MAP.inReview] === undefined ||
          attributes[QUESTION_ATTRIBUTE_MAP.inReview] === null)
      ) {
        toggleItemAttribute({ attribute: QUESTION_ATTRIBUTE_MAP.inReview, itemId: activeItemId })
      }
      if (item.type === QUESTION_TYPE_MAP.timedMatchingGrid && attributes[QUESTION_ATTRIBUTE_MAP.inReview]) {
        setItemAttribute({
          attribute: QUESTION_ATTRIBUTE_MAP.inReview,
          itemId: activeItemId,
          value: false,
        })
      }
      if (item.type === QUESTION_TYPE_MAP.timedMatchingGrid && !attributes.required) {
        setItemAttribute({
          attribute: 'required',
          itemId: activeItemId,
          value: true,
        })
      }
    }
  }

  onChangeItemType = item => {
    const { survey, activeItemId, changeItemType } = this.props
    changeItemType(survey, activeItemId, survey.questions[activeItemId].type, item.value)
  }

  onUpdateItemInputWidth = item => {
    const { updateItem, survey, activeItemId } = this.props
    const { questions } = survey
    const question = questions[activeItemId]
    const newItem = { ...question, attributes: { ...question.attributes, inputWidth: item.value } }
    updateItem(activeItemId, newItem)
  }

  onDeleteChoice = choiceId => {
    const { activeItemId, deleteChoice, survey } = this.props
    return () => {
      deleteChoice(survey, activeItemId, choiceId)
    }
  }

  onScrollToItem = ({ isIntro }) => {
    const { activeItemId } = this.props
    const element = document.getElementById(`question-${activeItemId}`)
    let offset = 120
    if (isIntro) offset += 16
    if (element) {
      scrollTo({ element, offset })
    }
  }

  onAddChoice = () => {
    const { activeItemId, survey, addChoice } = this.props
    const { questions } = survey
    const { type } = questions[activeItemId]
    addChoice(activeItemId, questions[activeItemId], null, type)
  }

  onUpdateUnitType = e => {
    const { activeItemId, updateUnitType } = this.props
    updateUnitType(activeItemId, e.target.value)
  }

  onAddOtherChoice = e => {
    const { activeItemId, addOtherChoice } = this.props
    addOtherChoice(activeItemId)
  }

  onMoveItem = direction => {
    const { activeItemId, moveItem, survey } = this.props
    return () => {
      const startIdx = survey.order[0].indexOf(activeItemId)
      let endIdx
      if (direction === 'up') {
        endIdx = startIdx - 1
      } else {
        endIdx = startIdx + 1
      }
      const currentQuestionId = survey.order[0][startIdx]
      const { sibling_id } = survey.questions[currentQuestionId]
      const selectedItemsArr = sibling_id ? survey.siblings?.[sibling_id] : null
      moveItem(survey, startIdx, endIdx, selectedItemsArr)
    }
  }

  onDuplicateItem = () => {
    const { activeItemId, duplicateItem, question } = this.props
    if (QUESTION_TYPES_WITH_CHOICES.includes(question.type)) {
      const { choices_order } = question
      const choiceQProps = { choicesOrder: choices_order }
      duplicateItem(activeItemId, choiceQProps)
    } else if (question.type === QUESTION_TYPE_MAP.matrix) {
      const { choices_order } = question
      const choiceQProps = { choicesOrder: choices_order }
      const { questions_order } = question
      const matrixProps = { questionsOrder: questions_order }
      duplicateItem(activeItemId, choiceQProps, matrixProps)
    } else {
      duplicateItem(activeItemId)
    }
  }

  onAddMatrixQuestion = () => {
    const { activeItemId, addMatrixQuestion } = this.props
    addMatrixQuestion(activeItemId, null)
  }

  onDeleteMatrixQuestion = subQuestionId => {
    const { survey, activeItemId, deleteMatrixQuestion } = this.props
    deleteMatrixQuestion(survey, activeItemId, subQuestionId)
  }

  onToggleRequiredQuestion = () => {
    const { toggleRequiredQuestion, survey, activeItemId } = this.props
    if (survey.questions[activeItemId]?.type === QUESTION_TYPE_MAP.timedMatchingGrid) return
    toggleRequiredQuestion(survey, activeItemId)
  }

  onToggleInputValidation = () => {
    const { toggleInputValidation, activeItemId } = this.props
    toggleInputValidation(activeItemId)
  }

  onToggleScoring = () => {
    const { survey, toggleQuestionScoring, activeItemId } = this.props
    const item = survey.questions[activeItemId]
    const { domains = [] } = item
    toggleQuestionScoring(activeItemId, domains)
  }

  onToggleOptOut = () => {
    const { survey, toggleQuestionOptOut, activeItemId } = this.props
    const item = survey.questions[activeItemId]
    const { domains = [] } = item
    toggleQuestionOptOut(activeItemId, domains)
  }

  onUpdateItemFormula = formula => {
    const { activeItemId, updateItemFormula } = this.props
    updateItemFormula(activeItemId, formula)
  }

  onUpdateItemComments = comments => {
    const { updateItem, survey, activeItemId } = this.props
    const question = survey.questions[activeItemId]
    updateItem(activeItemId, { ...question, comments })
  }

  onCreateNewDomain = label => {
    const { activeItemId, createNewDomainAndAddToQuestion } = this.props
    createNewDomainAndAddToQuestion(activeItemId, label)
  }

  onToggleDomain = domainId => {
    const { activeItemId, toggleQuestionDomain } = this.props
    toggleQuestionDomain(activeItemId, domainId)
  }

  render() {
    const {
      activeItemId,
      onDeleteItem,
      survey,
      toggleItemAttribute,
      setItemAttribute,
      updateItem,
      study,
      userId,
      isReadOnlyState,
    } = this.props
    const item = survey.questions[activeItemId]
    const { attributes, formula, sibling_id } = item
    const {
      hasScoring,
      hasOptOut,
      max_photo_limit: maxPhotoLimit,
      min_choices: minChoices,
      max_choices: maxChoices,
      min_choices_enabled: minChoicesEnabled,
      max_choices_enabled: maxChoicesEnabled,
    } = attributes
    const { domains } = survey
    const questionDomains = item.domains
    const qNumber = item.attributes.questionNumber
    const isIntro = item.type === QUESTION_TYPE_MAP.introduction
    const isMultiField = item.type === QUESTION_TYPE_MAP.multipleField
    const isSelectType = SELECT_QUESTION_TYPES.includes(item.type)
    const isSelectMultiple = item.type === QUESTION_TYPE_MAP.selectMultiple
    const isMatrixMulti = item.type === QUESTION_TYPE_MAP.matrix && item.attributes.allowMultipleAnswers
    const isFoodEntry = item.type === QUESTION_TYPE_MAP.foodEntry
    const isPhotoSubmission = item.type === QUESTION_TYPE_MAP.imageCapture
    const isQuestion = ![QUESTION_TYPE_MAP.introduction, QUESTION_TYPE_MAP.prompt].includes(item.type)
    const isFirstSibling = sibling_id ? survey.siblings?.[sibling_id]?.indexOf(activeItemId) === 0 : false
    const isSubsequentSibling = !!sibling_id && !isFirstSibling
    const isACQ7Type = item.type === QUESTION_TYPE_MAP.acq7
    const canHaveScoring = QUESTION_TYPES_WITH_SCORING.includes(item.type)
    const canHaveOptOut = QUESTION_TYPES_WITH_OPT_OUT.includes(item.type)
    const itemText = qNumber ? `Q${qNumber}` : isIntro ? 'Introduction Text' : 'Text'
    const isTimedMatchingGrid = item.type === QUESTION_TYPE_MAP.timedMatchingGrid
    const disabledRequired = [
      QUESTION_TYPE_MAP.introduction,
      QUESTION_TYPE_MAP.audioRecording,
      QUESTION_TYPE_MAP.prompt,
      QUESTION_TYPE_MAP.timedMatchingGrid,
    ].includes(item.type)
    const maxPhotoLimitPanelShown = isFoodEntry || isPhotoSubmission
    const toRenderLogicPanel = item.attributes.canHaveLogic && !isACQ7Type

    return (
      <div className='item-editor' onClick={() => this.onScrollToItem({ isIntro })}>
        <div className='editor-toolbar flexed'>
          <div className='flexed align-center'>
            <div className='question-number'>
              <h4>{itemText}</h4>
            </div>
            {!isIntro && (
              <>
                <Divider height='15px' width='1px' color='#cacaca' />
                <ValidationPanel
                  hasCharLimit={item.attributes.hasCharLimit}
                  disabledRequired={disabledRequired}
                  keepRequiredOnAppearance={isTimedMatchingGrid}
                  isMultiField={isMultiField}
                  onToggleInputValidation={this.onToggleInputValidation}
                  onToggleRequiredQuestion={this.onToggleRequiredQuestion}
                  required={isTimedMatchingGrid ? true : item.attributes.required}
                />
                <div className='toolbar-buttons button-list'>
                  <Button
                    id='clone-question'
                    className='far fa-clone hoverable-1'
                    onClick={this.onDuplicateItem}
                    noStyling
                  />
                  <Button
                    id='trash-icon'
                    className='fas fa-trash-alt hoverable-red'
                    onClick={onDeleteItem(activeItemId)}
                    noStyling
                  />
                  <i
                    className={`clickable fas fa-arrow-up${survey.order[0][1] === activeItemId || survey.order[0][0] === activeItemId || isSubsequentSibling
                      ? ' disabled'
                      : ''
                      }`}
                    onClick={this.onMoveItem('up')}
                  />
                  <i
                    className={`clickable fas fa-arrow-down${survey.order[0][survey.order[0].length - 1] === activeItemId || item.type === 'introduction' || isSubsequentSibling
                      ? ' disabled'
                      : ''
                      }`}
                    onClick={this.onMoveItem('down')}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {isIntro && (
          <div>
            <div className='panel'>{STRINGS.surveyIntroDescription}</div>
            <img
              src={!attributes[QUESTION_ATTRIBUTE_MAP.disableGreeting] ? introPic : introNoGreetingPic}
              alt='intro-sample'
            />
            <AttributePanel
              label={STRINGS.surveyIntroDisplayGreeting}
              checked={!attributes[QUESTION_ATTRIBUTE_MAP.disableGreeting]}
              onToggle={() => {
                toggleItemAttribute({ attribute: QUESTION_ATTRIBUTE_MAP.disableGreeting, itemId: activeItemId })
              }}
              disabled={isReadOnlyState}
            />
          </div>
        )}
        {isQuestion && (
          <>
            <QuestionPanel
              activeItemId={activeItemId}
              item={item}
              onAddOtherChoice={this.onAddOtherChoice}
              onChangeItemType={this.onChangeItemType}
              onDeleteChoice={this.onDeleteChoice}
              onAddChoice={this.onAddChoice}
              onAddMatrixQuestion={this.onAddMatrixQuestion}
              onDeleteMatrixQuestion={this.onDeleteMatrixQuestion}
              onUpdateUnitType={this.onUpdateUnitType}
              onUpdateItemInputWidth={this.onUpdateItemInputWidth}
              study={study}
              userId={userId}
              {...this.props}
            />
            {item.type === QUESTION_TYPE_MAP.timedMatchingGrid && (
              <TimedMatchingGridConfigPanel
                item={item}
                itemId={activeItemId}
                updateItem={updateItem}
                disabled={isReadOnlyState}>
                {canHaveScoring && (
                  <ScoringPanel
                    createNewDomain={this.onCreateNewDomain}
                    domains={domains}
                    hasAnswerValues={!isTimedMatchingGrid}
                    hasCalcDropdown={isSelectMultiple || isMatrixMulti}
                    hasScoring={hasScoring}
                    onToggleDomain={this.onToggleDomain}
                    onToggleScoring={this.onToggleScoring}
                    onUpdateItemFormula={this.onUpdateItemFormula}
                    questionDomains={questionDomains}
                    formula={formula}
                  />
                )}
              </TimedMatchingGridConfigPanel>
            )}
            {isSelectType && (
              <AttributePanel
                label={STRINGS.surveySelectDisplayCheckbox}
                checked={!attributes[QUESTION_ATTRIBUTE_MAP.disableButtons]}
                onToggle={() => {
                  toggleItemAttribute({ attribute: QUESTION_ATTRIBUTE_MAP.disableButtons, itemId: activeItemId })
                }}
              />
            )}
            {canHaveOptOut && (
              <AttributePanel
                hasOptOut={hasOptOut}
                heading={STRINGS.optOut}
                label={STRINGS.optOutDescription}
                checked={attributes[QUESTION_ATTRIBUTE_MAP.hasOptOut]}
                onToggle={() => {
                  toggleItemAttribute({ attribute: QUESTION_ATTRIBUTE_MAP.hasOptOut, itemId: activeItemId })
                }}
              />
            )}
            {canHaveScoring && !isTimedMatchingGrid && (
              <ScoringPanel
                createNewDomain={this.onCreateNewDomain}
                domains={domains}
                hasAnswerValues={!isTimedMatchingGrid}
                hasCalcDropdown={isSelectMultiple || isMatrixMulti}
                hasScoring={hasScoring}
                onToggleDomain={this.onToggleDomain}
                onToggleScoring={this.onToggleScoring}
                onUpdateItemFormula={this.onUpdateItemFormula}
                questionDomains={questionDomains}
                formula={formula}
              />
            )}
            {isSelectMultiple && (
              <SelectionRulesPanel
                minChoices={minChoices}
                maxChoices={maxChoices}
                totalOptions={item.choices_order ? item.choices_order.length : 0}
                required={attributes.required || false}
                minChoicesEnabled={minChoicesEnabled}
                maxChoicesEnabled={maxChoicesEnabled}
                onUpdateMinChoices={value => {
                  setItemAttribute({
                    attribute: QUESTION_ATTRIBUTE_MAP.minChoices,
                    itemId: activeItemId,
                    value,
                  })
                }}
                onUpdateMaxChoices={value => {
                  setItemAttribute({
                    attribute: QUESTION_ATTRIBUTE_MAP.maxChoices,
                    itemId: activeItemId,
                    value,
                  })
                }}
                onToggleMinChoicesEnabled={() => {
                  setItemAttribute({
                    attribute: QUESTION_ATTRIBUTE_MAP.minChoicesEnabled,
                    itemId: activeItemId,
                    value: !(minChoicesEnabled !== false),
                  })
                }}
                onToggleMaxChoicesEnabled={() => {
                  setItemAttribute({
                    attribute: QUESTION_ATTRIBUTE_MAP.maxChoicesEnabled,
                    itemId: activeItemId,
                    value: !(maxChoicesEnabled !== false),
                  })
                }}
              />
            )}
            {item.type === QUESTION_TYPE_MAP.matrix && (
              <CommentsPanel comments={item.comments || ''} onChange={this.onUpdateItemComments} />
            )}
          </>
        )}
        {item.type === QUESTION_TYPE_MAP.timer && (
          <div>
            <img src={timerPic} alt='timer-sample' />
          </div>
        )}
        {toRenderLogicPanel && <LogicPanelContainer itemId={activeItemId} />}
        {!isIntro ? (
          <InReviewPanel
            inReviewChecked={attributes[QUESTION_ATTRIBUTE_MAP.inReview]}
            onToggleInReviewChecked={() => {
              toggleItemAttribute({ attribute: QUESTION_ATTRIBUTE_MAP.inReview, itemId: activeItemId })
            }}
            study={study}
            userId={userId}
            questionType={item.type}
          />
        ) : null}
        {maxPhotoLimitPanelShown ? (
          <MaxPhotoLimitPanel
            study={study}
            userId={userId}
            maxPhotoLimit={maxPhotoLimit}
            onPlusClick={() =>
              setItemAttribute({
                attribute: QUESTION_ATTRIBUTE_MAP.maxPhotoLimit,
                itemId: activeItemId,
                value: maxPhotoLimit + 1,
              })
            }
            onMinusClick={() => {
              setItemAttribute({
                attribute: QUESTION_ATTRIBUTE_MAP.maxPhotoLimit,
                itemId: activeItemId,
                value: maxPhotoLimit - 1,
              })
            }}
            onSetDefaultValue={() => {
              setItemAttribute({
                attribute: QUESTION_ATTRIBUTE_MAP.maxPhotoLimit,
                itemId: activeItemId,
                value: MAX_PHOTO_LIMIT_FIELD_DEFAULT_VALUE,
              })
            }}
          />
        ) : null}
        {isQuestion && !isPhotoSubmission && !isTimedMatchingGrid && (
          <>
            <Divider color='#979797' margin='16px 20px' />
            <SiblingsPanel
              activeItemId={activeItemId}
              addSiblingSurveyItem={this.props.addSiblingSurveyItem}
              survey={survey}
              item={item}
              setActiveItemId={this.props.setActiveItemId}
            />
          </>
        )}
      </div>
    )
  }
}

ItemEditor.propTypes = {
  activeItemId: PropTypes.string,
  addChoice: PropTypes.func,
  addMatrixQuestion: PropTypes.func,
  addSiblingSurveyItem: PropTypes.func,
  addOtherChoice: PropTypes.func,
  changeItemType: PropTypes.func,
  deleteMatrixQuestion: PropTypes.func,
  createNewDomainAndAddToQuestion: PropTypes.func,
  deleteChoice: PropTypes.func,
  duplicateItem: PropTypes.func,
  item: PropTypes.object,
  itemId: PropTypes.string,
  moveItem: PropTypes.func,
  onDeleteItem: PropTypes.func,
  question: PropTypes.shape({
    choices_order: PropTypes.array,
    type: PropTypes.string,
  }),
  survey: PropTypes.object,
  setActiveItemId: PropTypes.func,
  toggleInputValidation: PropTypes.func,
  toggleItemAttribute: PropTypes.func,
  toggleQuestionDomain: PropTypes.func,
  toggleMatrixAllowMultiple: PropTypes.func,
  toggleRequiredQuestion: PropTypes.func,
  toggleQuestionScoring: PropTypes.func,
  updateItem: PropTypes.func,
  updateItemFormula: PropTypes.func,
  updateUnitType: PropTypes.func,
}

export default connect(null, itemEditorActions)(ItemEditor)
